import { Link } from "react-router";
import * as Icons from "lucide-react";
import { Check, ChevronDown, Minus } from "lucide-react";
import type { PublicService } from "@veo/types";
import { cn } from "@veo/ui";
import { fetchContent } from "../lib/api";
import { loadPage } from "../lib/page";
import { plainText } from "../lib/richtext";
import { RichText } from "../components/RichText";
import { Reveal, RevealItem, RevealList } from "../components/motion";
import { seoMeta } from "../lib/seo";
import { absoluteUrl, SITE_URL } from "../lib/site";
// Borrowed from the block renderers so a card behaves the same whether it is
// built in the page builder or hand-written in this route.
import { cardHover } from "../blocks/SectionRenderer";
import type { Route } from "./+types/service-detail";

type ServiceResponse = PublicService & { globalRiskDisclaimer: string };

export async function loader({ params, request }: Route.LoaderArgs) {
  const preview = new URL(request.url).searchParams.get("preview");
  const query = preview ? `?preview=${encodeURIComponent(preview)}` : "";

  return {
    service: await loadService(params.slug, query),
    isPreview: Boolean(preview),
  };
}

/**
 * A service that has moved or been merged leaves a redirect behind in the CMS.
 * The services endpoint knows nothing of redirects, so on a miss the old
 * address is checked there: `loadPage` throws the 301 when one exists, and the
 * original 404 stands when it does not.
 */
async function loadService(slug: string, query: string): Promise<ServiceResponse> {
  try {
    return await fetchContent<ServiceResponse>(`/public/services/${slug}${query}`);
  } catch (error) {
    if (error instanceof Response && error.status === 404) {
      await loadPage(`/services/${slug}`);
    }
    throw error;
  }
}

export function meta({ loaderData, params, matches }: Route.MetaArgs) {
  // React Router v8 names this `loaderData` in meta args (it was `data` in v7).
  if (!loaderData?.service) return [{ title: "Service — VEO Consultancy" }];

  const { service } = loaderData;
  const path = `/services/${params.slug}`;
  const canonical = service.seo?.canonicalUrl ?? absoluteUrl(path);

  return [
    ...seoMeta({
      matches,
      title: service.seo?.metaTitle ?? `${service.name} — VEO Consultancy`,
      description: service.seo?.metaDescription ?? service.summary,
      path,
      canonical,
      image: service.seo?.ogImageUrl ?? service.heroImage?.url,
      ogTitle: service.seo?.ogTitle,
      ogDescription: service.seo?.ogDescription,
      noIndex: service.seo?.noIndex,
    }),

    /**
     * Structured data for the service and its FAQs. Search engines use these
     * for rich results, which is the difference between a plain blue link and
     * one that shows the questions this page answers.
     *
     * `provider` points at the organisation declared once in root.tsx by `@id`
     * rather than describing it again here. Two descriptions of the same firm
     * that drift apart are worse than one, and a reference is how the two
     * blocks are understood as being about the same entity.
     */
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: service.name,
        description: service.summary,
        url: canonical,
        serviceType: service.name,
        areaServed: { "@type": "Country", name: "Kenya" },
        provider: { "@id": `${SITE_URL}/#organisation` },
        ...(service.heroImage?.url ? { image: service.heroImage.url } : {}),
        ...(service.faqs.length > 0
          ? {
              mainEntityOfPage: {
                "@type": "FAQPage",
                mainEntity: service.faqs.map((faq: PublicService["faqs"][number]) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: { "@type": "Answer", text: plainText(faq.answer) },
                })),
              },
            }
          : {}),
      },
    },

    /**
     * The trail Home → Services → this service.
     *
     * The page shows no breadcrumb of its own — it opens on a full-bleed hero,
     * where one would look out of place — but a result in search can still
     * display the path instead of a bare URL, and that only happens if the
     * relationship is stated somewhere. Here is the only place left to state it.
     */
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Services", item: absoluteUrl("/services") },
          { "@type": "ListItem", position: 3, name: service.name, item: canonical },
        ],
      },
    },
  ];
}


function Icon({ name, className }: { name: string; className?: string }) {
  const Component = (Icons[name as keyof typeof Icons] as typeof Icons.Circle) ?? Icons.Circle;
  return <Component aria-hidden className={className} />;
}

/**
 * How much prose it takes before "About this service" is worth setting in
 * columns.
 *
 * Three columns is a claim that there is enough here to need them. Two short
 * paragraphs split three ways is not a newspaper, it is three stubs with a lot
 * of white space underneath — so a thin introduction keeps the single readable
 * measure and only a substantial one goes wide. Measured in characters of
 * plain text, which is a blunt instrument but the right one: what matters is
 * the volume of words, not how many nodes the editor happened to make.
 *
 * Roughly a screen of text at the article measure. Every seeded service clears
 * it; a one-paragraph service written later will not, and should not.
 */
const COLUMNS_THRESHOLD = 600;

/**
 * The column flow, and the four rules that keep it readable.
 *
 * A multi-column block is one continuous run of text poured into three
 * vessels, so the breaks fall wherever the copy happens to reach the bottom of
 * a column — which is how a heading ends up stranded as the last line above
 * one, and a bullet ends up cut in half across the gap. Left alone that is
 * worse than a single column, not better:
 *
 *   · `break-after-avoid` on headings keeps a heading with what it introduces
 *   · `break-inside-avoid` on list items keeps a bullet whole
 *   · orphans/widows stop a paragraph leaving a single line behind or ahead
 *
 * The last of those is Chromium-only — Firefox has never implemented either
 * property. It is worth setting anyway: where it is honoured the text is
 * better, and where it is not the layout is exactly what it would have been.
 *
 * The ladder is 1 → 2 → 3 rather than straight to three. Three columns inside
 * a tablet's width leaves about 30 characters a line, and prose that narrow is
 * read a word at a time.
 */
const COLUMN_FLOW = cn(
  "md:columns-2 lg:columns-3 md:gap-x-12 lg:gap-x-14",
  "[&_h2]:break-after-avoid [&_h3]:break-after-avoid [&_h4]:break-after-avoid",
  "[&_li]:break-inside-avoid",
  "[&_p]:[orphans:2] [&_p]:[widows:2]",
);

/**
 * How many columns the audience takes, so a service's whole audience sits on
 * one row.
 *
 * These cards name the kinds of organisation a service is for, and they are
 * read as one set: four across says "any of these four", where the three-column
 * grid this replaced said "these three — oh, and this other one", with the
 * fourth card alone on a row of its own. Which card got stranded was an
 * accident of how many the editor happened to add.
 *
 * Two across from `sm` in every case. At `lg` four cards leave about 210px
 * apiece, which a title as long as "Companies Expanding into New Markets" can
 * still wrap into; narrower than that and it could not. Written out per count
 * because Tailwind emits only the classes it can read as literal text in the
 * source.
 *
 * Past four the single row stops being worth having — these cards each carry a
 * sentence or two, and six columns of that is six ribbons of text. Five and up
 * hold at three across and wrap; the CMS allows up to eight.
 */
const AUDIENCE_COLUMNS: Record<number, string> = {
  1: "",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

/**
 * How wide one process step is, so a service's whole sequence sits on one row.
 *
 * The steps are numbered and consecutive, which is a different kind of set from
 * the benefits above them: benefits are an unordered pile where a second row
 * costs nothing, whereas a process wrapped onto two rows asks the reader to
 * find step five below and to the left of step four. Reading order and the
 * numbering stop agreeing. So the row is sized to the number of steps rather
 * than fixed at four.
 *
 * The widths subtract each item's share of the gutter — `gap-10` is 2.5rem, and
 * n per row lose (n-1)/n of it each. Written out per count because Tailwind
 * emits only the classes it can read as literal text in the source.
 *
 * Five and six reach full width at `xl` and not before. At `lg` five steps
 * leave about 160px apiece, which is narrower than the step titles want; they
 * hold at three across until there is room, and three-then-two is a far better
 * failure than four-then-one.
 */
const STEP_WIDTHS: Record<number, string> = {
  2: "md:w-[calc(50%-1.25rem)]",
  3: "md:w-[calc(50%-1.25rem)] lg:w-[calc(33.333%-1.667rem)]",
  4: "md:w-[calc(50%-1.25rem)] lg:w-[calc(25%-1.875rem)]",
  5: "md:w-[calc(50%-1.25rem)] lg:w-[calc(33.333%-1.667rem)] xl:w-[calc(20%-2rem)]",
  6: "md:w-[calc(50%-1.25rem)] lg:w-[calc(33.333%-1.667rem)] xl:w-[calc(16.666%-2.083rem)]",
};

export default function ServiceDetail({ loaderData }: Route.ComponentProps) {
  const { service, isPreview } = loaderData;
  const disclaimer = service.riskDisclaimer || service.globalRiskDisclaimer;

  /**
   * The introduction, falling back to the description — the same preference
   * the section has always expressed, said once instead of as two branches
   * rendering identical markup.
   */
  const about = service.introduction?.content?.length
    ? service.introduction
    : service.description?.content?.length
      ? service.description
      : null;

  const aboutInColumns = plainText(about).length >= COLUMNS_THRESHOLD;

  return (
    <>
      {isPreview && (
        <div
          role="status"
          className="bg-gold-500 px-4 py-2 text-center text-sm font-semibold text-navy-900"
        >
          Preview — this service is not published. Only people with this link can see it.
        </div>
      )}

      <main id="main">
        {/* 1 · Hero ---------------------------------------------------- */}
        <section className="relative overflow-hidden bg-navy-900 text-cream-100">
          {service.heroImage && (
            <>
              <img
                src={service.heroImage.url}
                alt=""
                className="absolute inset-0 size-full object-cover"
                fetchPriority="high"
              />
              {/* The overlay is what keeps the headline readable over an
                  arbitrary photograph an editor uploads later. */}
              <div className="absolute inset-0 bg-navy-900/80" aria-hidden />
            </>
          )}

          <div className="container-site relative section-y">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-xs text-navy-300">
                <li>
                  <Link to="/" className="hover:text-cream-100">
                    Home
                  </Link>
                </li>
                <li aria-hidden>/</li>
                <li>
                  <Link to="/services" className="hover:text-cream-100">
                    Services
                  </Link>
                </li>
                <li aria-hidden>/</li>
                <li aria-current="page" className="text-gold-300">
                  {service.name}
                </li>
              </ol>
            </nav>

            <div className="max-w-3xl">
              {service.minInvestment && (
                <p className="eyebrow text-gold-300">{service.minInvestment}</p>
              )}
              <h1 className="mt-4 text-6xl text-cream-50">{service.name}</h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-navy-100 md:text-xl">
                {service.summary}
              </p>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to={service.cta.primaryButton.href}
                  className="rounded-md bg-gold-500 px-6 py-3 text-sm font-semibold text-navy-900 transition-colors hover:bg-gold-400"
                >
                  {service.cta.primaryButton.label}
                </Link>
                <a
                  href="#how-it-works"
                  className="rounded-md px-6 py-3 text-sm font-semibold text-cream-100 ring-1 ring-inset ring-cream-100/30 transition-colors hover:bg-cream-100/10"
                >
                  How it works
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 2 · Introduction -------------------------------------------- */}
        {about && (
          <section className="bg-surface-0 textured">
            <div className="container-site section-y">
              {/*
                A long introduction is set across columns and takes the full
                page width; a short one keeps the article measure it has always
                had. The wrapper's width has to move with the layout — three
                columns inside a 46rem measure would be three gutters wide.
              */}
              <Reveal className={aboutInColumns ? undefined : "container-content"}>
                <p className="eyebrow">About this service</p>
                <RichText doc={about} className={cn("mt-6", aboutInColumns && COLUMN_FLOW)} />
              </Reveal>
            </div>
          </section>
        )}

        {/* 3 · Key benefits -------------------------------------------- */}
        {service.benefits.length > 0 && (
          <section className="bg-surface-100 textured">
            <div className="container-site section-y">
              <Reveal>
                <p className="eyebrow">Key benefits</p>
                <h2 className="rule-accent mt-3 text-4xl">What you get from working with us</h2>
              </Reveal>

              {/*
                  Flex rather than grid, so an odd count does not leave a hole.

                  A grid places a trailing item in column one and leaves the
                  rest of the row blank — with five benefits in two columns
                  that is a half-width void under the last card. Wrapping with
                  `justify-center` puts the remainder in the middle instead and
                  splits the leftover space either side of it.

                  The widths subtract their share of the gap: two per row lose
                  half of one gap each, so 50% - 0.75rem against gap-6.
                */}
              <RevealList className="mt-12 flex flex-wrap justify-center gap-6">
                {service.benefits.map((benefit) => (
                  <RevealItem
                    key={benefit.id}
                    className={cn(
                      "w-full sm:w-[calc(50%-0.75rem)]",
                      "rounded-lg bg-white p-7 shadow-card",
                      cardHover(false),
                    )}
                  >
                    {benefit.icon && (
                      <span className="inline-flex rounded-md bg-navy-900 p-2.5 text-gold-300">
                        <Icon name={benefit.icon} className="size-5" />
                      </span>
                    )}
                    <h3 className="mt-4 text-xl">{benefit.title}</h3>
                    {benefit.description && (
                      <p className="mt-2 text-base leading-relaxed text-ink-600">
                        {benefit.description}
                      </p>
                    )}
                  </RevealItem>
                ))}
              </RevealList>
            </div>
          </section>
        )}

        {/* 4 · Who this is for ----------------------------------------- */}
        {service.audience.length > 0 && (
          <section className="bg-surface-0 textured">
            <div className="container-site section-y">
              <Reveal>
                <p className="eyebrow">Who this is for</p>
                <h2 className="rule-accent mt-3 text-4xl">Who this service is built for</h2>
              </Reveal>

              <RevealList
                className={cn(
                  "mt-12 grid gap-x-10 gap-y-8",
                  AUDIENCE_COLUMNS[service.audience.length] ?? "sm:grid-cols-2 lg:grid-cols-3",
                )}
              >
                {service.audience.map((item) => (
                  <RevealItem key={item.title}>
                    {item.icon && <Icon name={item.icon} className="size-6 text-gold-600" />}
                    <h3 className="mt-4 font-display text-xl text-navy-900">{item.title}</h3>
                    {item.description && (
                      <p className="mt-2 text-base leading-relaxed text-ink-600">
                        {item.description}
                      </p>
                    )}
                  </RevealItem>
                ))}
              </RevealList>
            </div>
          </section>
        )}

        {/* 5 · How it works -------------------------------------------- */}
        {service.processSteps.length > 0 && (
          <section id="how-it-works" className="bg-navy-900 textured-dark text-cream-100">
            <div className="container-site section-y">
              <Reveal>
                <p className="eyebrow text-gold-300">The process</p>
                <h2 className="mt-3 text-3xl text-cream-50 md:text-4xl">How this service works</h2>
                <span aria-hidden className="mt-4 block h-0.5 w-12 bg-gold-500" />
              </Reveal>

              {/* Flex rather than grid, so the breakpoints where the sequence
                  does still wrap centre the remainder instead of leaving a
                  hole — three then two, not three then one adrift on the left.
                  See STEP_WIDTHS for how the row is sized. */}
              <RevealList as="ol" className="mt-14 flex flex-wrap justify-center gap-10">
                {service.processSteps.map((step) => (
                  <RevealItem
                    key={step.id}
                    className={cn(
                      "relative w-full",
                      STEP_WIDTHS[service.processSteps.length] ?? STEP_WIDTHS[4],
                    )}
                  >
                    {/*
                      Gold rather than the navy-700 this used to be. On a
                      navy-900 ground that was about 1.4:1 against the
                      background — a numeral you could only find by knowing
                      where to look, which is not a ghost numeral, it is a
                      missing one. Gold clears 8:1 and matches how the CMS
                      process block has always drawn its steps, so the same
                      section looks the same whether it is built in the page
                      builder or hand-written here.

                      `tabular-nums` because these are stacked in a row: with
                      proportional figures "01" and "11" set to different
                      widths and the column of numerals reads as ragged.
                    */}
                    <span
                      aria-hidden
                      className="font-display text-5xl font-semibold tabular-nums text-gold-400"
                    >
                      {String(step.stepNumber).padStart(2, "0")}
                    </span>
                    <h3 className="mt-3 font-display text-xl text-cream-50">{step.title}</h3>
                    {step.description && (
                      <p className="mt-2 text-sm leading-relaxed text-navy-200">
                        {step.description}
                      </p>
                    )}
                    {step.duration && (
                      <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-gold-300">
                        {step.duration}
                      </p>
                    )}
                  </RevealItem>
                ))}
              </RevealList>
            </div>
          </section>
        )}

        {/* 6 · What is included ---------------------------------------- */}
        {service.inclusions.length > 0 && (
          <section className="bg-surface-0 textured">
            <div className="container-site section-y">
              <Reveal>
                <p className="eyebrow">What is included</p>
                <h2 className="rule-accent mt-3 text-4xl">Exactly what you receive</h2>
              </Reveal>

              <RevealList className="container-content mt-12 space-y-4">
                {service.inclusions.map((item) => (
                  <RevealItem
                    key={item.label}
                    className={cn(
                      "flex gap-4 rounded-lg border p-5",
                      "transition-shadow duration-300 hover:shadow-md motion-reduce:transition-none",
                      item.isIncluded
                        ? "border-growth-200 bg-growth-50/40"
                        : "border-ink-200 bg-ink-50",
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full",
                        item.isIncluded ? "bg-growth-600 text-white" : "bg-ink-300 text-ink-700",
                      )}
                    >
                      {item.isIncluded ? (
                        <Check aria-hidden className="size-3.5" />
                      ) : (
                        <Minus aria-hidden className="size-3.5" />
                      )}
                      <span className="sr-only">
                        {item.isIncluded ? "Included" : "Not included"}
                      </span>
                    </span>
                    <span>
                      <span
                        className={cn(
                          "block font-medium",
                          item.isIncluded ? "text-navy-900" : "text-ink-600",
                        )}
                      >
                        {item.label}
                      </span>
                      {item.detail && (
                        <span className="mt-1 block text-sm leading-relaxed text-ink-600">
                          {item.detail}
                        </span>
                      )}
                    </span>
                  </RevealItem>
                ))}
              </RevealList>
            </div>
          </section>
        )}

        {/* 7 · FAQs ----------------------------------------------------- */}
        {service.faqs.length > 0 && (
          <section className="bg-surface-100 textured">
            <div className="container-site section-y">
              <Reveal>
                <p className="eyebrow">Questions</p>
                <h2 className="rule-accent mt-3 text-4xl">Frequently asked questions</h2>
              </Reveal>

              <div className="container-content mt-12 space-y-3">
                {service.faqs.map((faq) => (
                  // Native <details> gives keyboard operation, screen-reader
                  // semantics and in-page find for free. A custom accordion
                  // would have to reimplement all three, usually incompletely.
                  <details
                    key={faq.id}
                    className={cn(
                      "group rounded-lg bg-white px-6 py-5 shadow-sm",
                      "transition-[box-shadow,background-color] duration-200 motion-reduce:transition-none",
                      "hover:bg-navy-100 hover:shadow-md focus-within:bg-navy-100 focus-within:shadow-md",
                    )}
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display text-lg text-navy-900 marker:content-['']">
                      {faq.question}
                      <ChevronDown
                        aria-hidden
                        className="size-5 shrink-0 text-gold-600 transition-transform group-open:rotate-180"
                      />
                    </summary>
                    <div className="mt-4 border-t border-ink-100 pt-4">
                      <RichText doc={faq.answer} />
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 8 · Related insights ----------------------------------------- */}
        {service.relatedPosts.length > 0 && (
          <section className="bg-surface-0 textured">
            <div className="container-site section-y">
              <Reveal className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="eyebrow">Related insights</p>
                  <h2 className="mt-3 text-4xl">Further reading</h2>
                </div>
                <Link
                  to="/insights"
                  className="text-sm font-semibold text-navy-700 underline decoration-gold-500 underline-offset-4"
                >
                  All insights
                </Link>
              </Reveal>

              <RevealList className="mt-10 grid gap-8 md:grid-cols-3">
                {service.relatedPosts.map((post) => (
                  <RevealItem key={post.id}>
                    <article>
                      <Link to={`/insights/${post.slug}`} className="group block">
                        {post.coverImageUrl && (
                          <img
                            src={post.coverImageUrl}
                            alt=""
                            loading="lazy"
                            className="mb-4 aspect-[16/10] w-full rounded-lg object-cover shadow-sm transition-shadow duration-300 group-hover:shadow-lg motion-reduce:transition-none"
                          />
                        )}
                        <h3 className="font-display text-xl text-navy-900 group-hover:underline group-hover:decoration-gold-500 group-hover:underline-offset-4">
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p className="mt-2 text-sm leading-relaxed text-ink-600">
                            {post.excerpt}
                          </p>
                        )}
                        <p className="mt-3 text-xs text-ink-500">
                          {post.author}
                          {post.readingMinutes ? ` · ${post.readingMinutes} min read` : ""}
                        </p>
                      </Link>
                    </article>
                  </RevealItem>
                ))}
              </RevealList>
            </div>
          </section>
        )}

        {/* 9 · Consultation CTA ----------------------------------------- */}
        <section className="bg-navy-800 text-cream-100">
          <div className="container-site section-y">
            <Reveal className="container-content text-center">
              <h2 className="text-3xl text-cream-50 md:text-4xl">{service.cta.heading}</h2>
              {service.cta.body && (
                <p className="mt-4 text-lg leading-relaxed text-navy-100">{service.cta.body}</p>
              )}

              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link
                  to={service.cta.primaryButton.href}
                  className="rounded-md bg-gold-500 px-7 py-3.5 text-sm font-semibold text-navy-900 transition-colors hover:bg-gold-400"
                >
                  {service.cta.primaryButton.label}
                </Link>
                {service.cta.secondaryButton && (
                  <Link
                    to={service.cta.secondaryButton.href}
                    className="rounded-md px-7 py-3.5 text-sm font-semibold text-cream-100 ring-1 ring-inset ring-cream-100/30 hover:bg-cream-100/10"
                  >
                    {service.cta.secondaryButton.label}
                  </Link>
                )}
              </div>

              {service.testimonials[0] && (
                <figure className="mt-14 border-t border-navy-600 pt-10">
                  <blockquote className="font-display text-xl italic leading-relaxed text-cream-50 md:text-2xl">
                    “{service.testimonials[0].quote}”
                  </blockquote>
                  <figcaption className="mt-4 text-sm text-navy-200">
                    {service.testimonials[0].authorName}
                    {service.testimonials[0].authorTitle &&
                      `, ${service.testimonials[0].authorTitle}`}
                    {service.testimonials[0].authorCompany &&
                      ` · ${service.testimonials[0].authorCompany}`}
                  </figcaption>
                </figure>
              )}
            </Reveal>
          </div>
        </section>

        {/* 10 · Risk disclaimer ----------------------------------------- */}
        {disclaimer && (
          <section className="bg-surface-0 textured">
            <div className="container-site py-12">
              <div className="container-content rounded-lg border border-gold-300 bg-gold-50 p-6">
                <h2 className="flex items-center gap-2 font-display text-lg text-navy-900">
                  <Icons.TriangleAlert aria-hidden className="size-5 text-gold-600" />
                  Important information
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-ink-700">{disclaimer}</p>
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}
